import React, { useState } from "react";
import { CalendarClock, Home } from "lucide-react";
import PropertyInlineSummary from "../components/PropertyInlineSummary";
import RentedPropertyDetailsModal from "../components/RentedPropertyDetailsModal";
import { IRentedPropertyDetail } from "../types/ownerDashboard.types";
import { buildPropertyListingView } from "../utils/rentedPropertyDisplay";
import {
  getExpectedVacateLabel,
  getPropertyStatusLabel,
  shouldShowVacatingIndicator,
} from "../utils/vacatingDisplay";

interface RentedPropertyCardProps {
  property: IRentedPropertyDetail;
}

export default function RentedPropertyCard({ property }: RentedPropertyCardProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const isVacating = shouldShowVacatingIndicator(property);
  const statusLabel = getPropertyStatusLabel(property);
  const vacateLabel = isVacating
    ? getExpectedVacateLabel(property.expected_vacate_date)
    : null;
  const title = property.title?.trim() || "Imóvel alugado";
  const listing = buildPropertyListingView(property);

  const statusClassName = isVacating
    ? "border-amber-500/40 bg-amber-500/15 text-amber-400"
    : "border-emerald-500/40 bg-emerald-500/15 text-emerald-400";

  return (
    <>
      <article
        className="flex w-full flex-col gap-3 rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 sm:flex-row sm:items-start sm:justify-between"
        data-testid={`rented-property-card-${property.property_id}`}
      >
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start gap-2">
            <Home className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" aria-hidden />
            <div className="min-w-0 flex-1 space-y-2">
              <h3 className="truncate text-sm font-semibold text-[#f0f6fc]">{title}</h3>
              <PropertyInlineSummary listing={listing} />
              <button
                type="button"
                onClick={() => setIsDetailsOpen(true)}
                className="inline-flex items-center rounded-full border border-[#6ea8ff]/35 px-3 py-1 text-[0.65rem] font-semibold text-[#6ea8ff] transition-colors hover:border-[#6ea8ff]/55 hover:bg-[#6ea8ff]/10"
                data-testid={`rented-property-details-button-${property.property_id}`}
              >
                Mais detalhes
              </button>
            </div>
          </div>

          {vacateLabel && (
            <div
              className="flex items-center gap-2 text-xs text-amber-300/90"
              data-testid={`rented-property-vacate-date-${property.property_id}`}
            >
              <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{vacateLabel}</span>
            </div>
          )}
        </div>

        <span
          className={`shrink-0 self-start rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold ${statusClassName}`}
          data-testid={`rented-property-status-${property.property_id}`}
        >
          {statusLabel}
        </span>
      </article>

      {isDetailsOpen && (
        <RentedPropertyDetailsModal
          title={title}
          detail={property}
          onClose={() => setIsDetailsOpen(false)}
        />
      )}
    </>
  );
}
